<script lang="ts" setup>
const { t: $t } = useI18n()

import type { ApiCustomerModuleMachineryPartnerSalesGetDetails } from '~/layers/customer-module/types'

const props = defineProps<{ machinery?: ApiCustomerModuleMachineryPartnerSalesGetDetails | null, isLoading?: boolean }>()

const { hasAnyAssignedRole } = useAuthorization()
const isPartnerSales = hasAnyAssignedRole(['partnerSales'])

const isReserved = computed(() => props.machinery?.relatedOfferPositions.some(({ relatedOffer }) => {
  return !relatedOffer.isCancelled
    && relatedOffer.type === 'sale'
    && relatedOffer.status === 'order'
}))

const machineryReleaseCondition = computed((): OneDataColumn<ApiCustomerModuleMachineryPartnerSalesGetDetails>[] => [
  {
    title: $t('customerModule.machinery.details.partnerSales.partnerReleaseColumn.isSpecialOffer.title'),
    key: 'partnerConditionsForPartnersThatCanSellMe',
    render: ({ partnerConditionsForPartnersThatCanSellMe }) => {
      const isSpecialOffer = !!partnerConditionsForPartnersThatCanSellMe[0]?.isSpecialOffer
      return $t(`customerModule.machinery.details.partnerSales.partnerReleaseColumn.isSpecialOffer.${isSpecialOffer}`)
    },
    isVisible: ({ partnerConditionsForPartnersThatCanSellMe }) => !!partnerConditionsForPartnersThatCanSellMe[0]?.isSpecialOffer,
  },
  {
    title: $t('customerModule.machinery.details.partnerSales.partnerReleaseColumn.priceRecommendedToSellFor'),
    key: 'partnerConditionsForPartnersThatCanSellMe',
    dataMinWidth: 100,
    render: ({ priceRecommendedToSellFor }) => priceRecommendedToSellFor ? useDisplayNumberAsCurrency(priceRecommendedToSellFor) : $t('customerModule.empty.placeholder'),
  },
  {
    title: $t('customerModule.machinery.details.partnerSales.partnerReleaseColumn.pricePartnerHasToPurchaseFor', { currency: 'EUR' }),
    key: 'partnerConditionsForPartnersThatCanSellMe',
    dataMinWidth: 100,
    render: ({ partnerConditionsForPartnersThatCanSellMe }) => partnerConditionsForPartnersThatCanSellMe[0]?.pricePartnerHasToPurchaseFor ? useDisplayNumberAsCurrency(partnerConditionsForPartnersThatCanSellMe[0].pricePartnerHasToPurchaseFor) : $t('customerModule.empty.placeholder'),
  },
  {
    title: $t('customerModule.machinery.details.partnerSales.partnerReleaseColumn.endCustomerSellingPrice', { currency: 'EUR' }),
    key: 'partnerConditionsForPartnersThatCanSellMe',
    dataMinWidth: 100,
    render: ({ endCustomerSellingPrice }) => endCustomerSellingPrice ? useDisplayNumberAsCurrency(endCustomerSellingPrice) : $t('customerModule.empty.placeholder'),
  },
  {
    title: $t('customerModule.machinery.details.partnerSales.partnerReleaseColumn.dealerSellingPrice', { currency: 'EUR' }),
    key: 'partnerConditionsForPartnersThatCanSellMe',
    dataMinWidth: 100,
    render: ({ dealerSellingPrice }) => dealerSellingPrice ? useDisplayNumberAsCurrency(dealerSellingPrice) : $t('customerModule.empty.placeholder'),
  },
  {
    title: $t('customerModule.machinery.details.partnerSales.partnerReleaseColumn.note'),
    key: 'partnerConditionsForPartnersThatCanSellMe',
    render: ({ partnerConditionsForPartnersThatCanSellMe }) => partnerConditionsForPartnersThatCanSellMe[0]?.note,
    hideIfNullish: true,
  },
])
</script>

<template>
  <CustomerModuleDataCard :title="$t('customerModule.machinery.details.partnerSales.releaseCondition')" :is-loading="isLoading">
    <div v-if="isPartnerSales && isReserved" class="text-orange-500 text-base mb-2">
      <Icon name="material-symbols:warning-outline" />
      {{ $t('customerModule.machinery.reserved') }}
      <NuxtLink href="mailto:sales@hanselmann.de" class="text-blue-500 hover:underline">
        sales@hanselmann.de
      </NuxtLink>
    </div>
    <TableOneDataColumn :config="{ data: machinery, columns: machineryReleaseCondition }" class="text-base" />
  </CustomerModuleDataCard>
</template>
